import { type JSX, useState, useEffect } from 'react';
import Typography from 'carbon-react/esm/components/typography';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import { Checkbox } from 'carbon-react/esm/components/checkbox';
import Dialog from 'carbon-react/esm/components/dialog';
import Textbox from 'carbon-react/esm/components/textbox';
import { Select, Option } from 'carbon-react/esm/components/select';
import { Collaborator, EmptyCollaborator } from '../../../../types/collaborator.types';
import { useProfilesContext } from '../../../../context/useProfilesContext';

interface EditCollaboratorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedCollaborator: Collaborator) => Promise<{ success: boolean; error?: string }>;
  collaborator: Collaborator;
  onSuccess?: (message: string) => void;
  onError?: (error: unknown) => void;
}

const EditCollaboratorDialog = ({
  isOpen,
  onClose,
  onSave,
  collaborator,
  onSuccess,
  onError,
}: EditCollaboratorDialogProps): JSX.Element | null => {
  const { profiles } = useProfilesContext();
  const [formData, setFormData] = useState<Collaborator>(collaborator || EmptyCollaborator);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (collaborator) {
      setFormData(collaborator);
    }
  }, [collaborator]);

  // Réinitialiser le formulaire à la fermeture de la dialog
  useEffect(() => {
    if (!isOpen) {
      setFormData(collaborator || EmptyCollaborator);
      setApiError('');
    }
  }, [isOpen, collaborator]);

  const handleChange = (field: keyof Collaborator, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setApiError('');
  };

  const handleSubmit = async () => {
    try {
      const result = await onSave(formData);

      if (result.success) {
        const successMessage = `Les informations du collaborateur ${formData.firstName} ${formData.lastName} ont été modifiées avec succès`;
        onClose();
        onSuccess?.(successMessage);
      } else {
        const errorMessage =
          result.error || 'Une erreur est survenue lors de la mise à jour du collaborateur';
        setApiError(errorMessage);
        onError?.(new Error(errorMessage));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Une erreur est survenue lors de la mise à jour du collaborateur';
      setApiError(errorMessage);
      onError?.(err);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onCancel={onClose} title="Modifier un collaborateur">
        <Box display={'flex'} flexDirection={'column'} gap={3} mb={3}>
          <Box display={'flex'} flexDirection={'column'} gap={2} mb={3}>
            <Typography variant="h3">Informations</Typography>
            <Textbox
              disabled
              label="Prénom"
              value={formData.firstName}
              onChange={e => handleChange('firstName', e.target.value)}
            />
            <Textbox
              disabled
              required
              label="Nom"
              value={formData.lastName}
              onChange={e => handleChange('lastName', e.target.value)}
            />
            <Textbox
              disabled
              required
              label="Email"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
            />
          </Box>

          <Box display={'flex'} flexDirection={'column'} gap={3} mb={2}>
            <Typography variant="h3">Accès</Typography>
            <Select
              label="Profil"
              value={formData.profileId}
              onChange={e => handleChange('profileId', e.target.value)}
              placeholder="Sélectionnez un profil"
            >
              {profiles.map(profile => (
                <Option key={profile.id} text={profile.name} value={profile.id} />
              ))}
            </Select>
            <Checkbox
              label="Actif"
              checked={formData.isActive}
              onChange={() => handleChange('isActive', !formData.isActive)}
            />
          </Box>
        </Box>

        {apiError && (
          <Box mb={2}>
            <Typography variant="p">{apiError}</Typography>
          </Box>
        )}

        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onClose} variantType="secondary" size="small" mr={2}>
            Annuler
          </Button>

          <Button onClick={handleSubmit} variantType="primary" size="small">
            Valider
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default EditCollaboratorDialog;
